import AudioKey from "./AudioKey";

export const KEY_UNKNOWN = 255;

export const MAJOR_MODE_PATTERN = [2, 2, 1, 2, 2, 2, 1];

export const MODE_MAJOR = 0;
export const MODE_MINOR = 5;

export const letters = ["C", "D", "E", "F", "G", "A", "B"];

export const pitchNames = [
	["C"],
	["D-flat", "C-sharp"],
	["D"],
	["E-flat", "D-sharp"],
	["E"],
	["F", "E-sharp"],
	["F-sharp", "G-flat"],
	["G"],
	["A-flat", "G-sharp"],
	["A"],
	["B-flat", "A-sharp"],
	["B", "C-flat"]
];

export const modes = [
	"major", // ionian
	"dorian",
	"phrygian",
	"lydian",
	"mixolydian",
	"minor", // aeolian
	"locrian"
];

export const modesWithMinorQuality = [
	"dorian",
	"phrygian",
	"minor",
	"locrian"
];

const symbols = {
	flat: "♭",
	sharp: "♯"
};

// Performance
var _pitchesSortedByFifth = null;
var _basicKeys = null;
var _allKeys = null;

// Id methods

export function numericKey(pitch, mode) {
	return (pitch << 4) + mode;
}

export function numericKeyValues(numericKey) {
	let pitch = numericKey >> 4;
	let mode = numericKey & 0x0f;

	return { pitch, mode };
}

export function segmentedNumber(pitch, mode) {
	return `${pitch}-${mode}`;
}

export function segmentedNumberValues(segmentedNumber) {
	return segmentedNumber.split("-")
		.map((n) => parseInt(n, 10));
}

export function keyIsUnknown(numericKey) {
	return typeof numericKey !== "number" ||
		isNaN(numericKey) ||
		numericKey >= KEY_UNKNOWN;
}

// Generated lists

export function pitchesSortedByFifth() {
	if (_pitchesSortedByFifth) {
		return _pitchesSortedByFifth;
	}

	let list = [];

	let fifthOffset = function(index, direction) {
		return clampPitch(index * direction * 5);
	};

	// D-flat to F

	for (let i = 1; i < 6; i++) {
		let offset = fifthOffset((6 - i), 1);
		list.push(offset);
	}

	// C to F-sharp

	for (let i = 0; i < 7; i++) {
		let offset = fifthOffset(i, -1);
		list.push(offset);
	}

	_pitchesSortedByFifth = list;
	return list;
}

export function basicKeys() {
	if (_basicKeys) {
		return _basicKeys;
	}

	let keys = [];

	pitchesSortedByFifth().forEach((p) => {
		let majorKey = new AudioKey(p, MODE_MAJOR);
		keys.push(majorKey);
		keys.push(getRelativeKey(majorKey));
	});

	_basicKeys = keys;
	return keys;
}

export function allKeys() {
	if (_allKeys) {
		return _allKeys;
	}

	let keys = [];

	let numPitches = pitchNames.length;
	let numModes = modes.length;

	for (let pitch = 0; pitch < numPitches; pitch++) {
		for (let mode = 0; mode < numModes; mode++) {
			keys.push(new AudioKey(pitch, mode));
		}
	}

	_allKeys = keys;
	return keys;
}

// Retrieving pitch and key objects

export function getPitchByName(pitchName) {
	let pn = ("" + pitchName).toLowerCase().trim();

	for (let pitch = 0; pitch < pitchNames.length; pitch++) {
		let names = pitchNames[pitch];

		for (let i = 0; i < names.length; i++) {
			let name = names[i];

			if (name.toLowerCase() === pn) {
				return pitch;
			}
		}
	}

	return -1;
}

export function getModeByName(modeName) {
	let mn = ("" + modeName).toLowerCase().trim();
	return modes.indexOf(mn);
}

export function getKeyByName(name) {
	let [pitchName, modeName] = ("" + name).trim().split(" ");
	let pitch = getPitchByName(pitchName);
	let mode = getModeByName(modeName);

	if (pitch < 0 || mode < 0) {
		return null;
	}

	return new AudioKey(pitch, mode);
}

export function getKeyByUrlName(urlName) {
	let name = getKeyNameFromUrlName(urlName);
	return getKeyByName(name);
}

export function getKeyByNumericKey(numericKey) {
	if (typeof numericKey !== "number") {
		return null;
	}

	let { pitch, mode } = numericKeyValues(numericKey);

	if (pitch < 0 || mode < 0) {
		return null;
	}

	return new AudioKey(pitch, mode);
}

// Attributes

export function getKeyUrlName(name) {
	return name.replace(/-/g, "").replace(/\s+/g, "-").toLowerCase();
}

function getKeyNameFromUrlName(urlName) {
	let n = urlName.replace(/-/g, " ").replace(/flat/, "-flat").replace(/sharp/, "-sharp");
	return n.charAt(0).toUpperCase() + n.substr(1);
}

export function getRichPitchName(pitchName) {
	if (!pitchName) {
		return "";
	}

	let [r, a] = pitchName.split("-");

	if (a) {
		return r + symbols[a];
	}

	return pitchName;
}

// Music theory

export function getRelativeKey(k) {
	// Works only for major or minor modes

	let { pitch, mode } = k;
	let difference = -3;
	let newMode = MODE_MINOR;

	if (mode === MODE_MINOR) {
		difference = 3;
		newMode = MODE_MAJOR;
	}

	else if (mode !== MODE_MAJOR) {
		return null;
	}

	let newPitch = pitch + difference;
	return new AudioKey(clampPitch(newPitch), newMode);
}

export function getRelativeMajor(key) {
	// Works for keys of all modes
	let { pitch, mode } = key;
	return new AudioKey(getRelativeMajorPitch(pitch, mode), MODE_MAJOR);
}

export function getRelativeMajorPitch(pitch, mode) {
	// Works for keys of all modes

	if (mode === MODE_MAJOR) {
		// Return itself
		return pitch;
	}

	let difference = MAJOR_MODE_PATTERN.slice(0, mode).reduce((a, v) => a + v, 0);
	return clampPitch(pitch - difference);
}

function getScalePattern(mode = MODE_MAJOR) {
	if (typeof mode !== "number") {
		return [];
	}

	return MAJOR_MODE_PATTERN.slice(mode)
		.concat(MAJOR_MODE_PATTERN.slice(0, mode));
}

export function getScalePitches(pitch, mode) {
	if (typeof pitch !== "number" || typeof mode !== "number") {
		return [];
	}

	let pitches = [pitch];
	let pattern = getScalePattern(mode).slice(0, -1);

	pattern.forEach((interval) => {
		pitch += interval;
		pitches.push(clampPitch(pitch));
	});

	return pitches;
}

export function modeHasMinorQuality(mode) {
	return modesWithMinorQuality.indexOf(modes[mode]) >= 0;
}

// Utility

function clampPitch(pitch) {
	let numPitches = pitchNames.length;

	while (pitch < 0) {
		pitch += numPitches;
	}

	while (pitch > numPitches - 1) {
		pitch -= numPitches;
	}

	return Math.abs(pitch);
}
